import { useState } from 'react';
import logoIcon from '../../assets/images/standalone_logo.png';
import logoFull from '../../assets/images/logo_aktivity.png';

/**
 * Open Question Activity Component
 * Clean UI design with text input and circular timer
 */

const OpenQuestionActivity = ({
  activity,
  sessionCode,
  sessionName = 'Live Session',
  trainerName = 'Trainer',
  timeRemaining,
  onSubmit,
  onBackToList,
  showResults = false,
  userResponse = '',
  isActivityEnded = false,
}) => {
  // Extract the response text - handle both string and object formats
  const extractResponseText = (response) => {
    if (!response) return '';
    if (typeof response === 'string') return response;
    if (typeof response === 'object') {
      // Handle various response object structures
      return response.response || response.text || response.answer || '';
    }
    return '';
  };

  const initialResponse = extractResponseText(userResponse);
  const [questionResponse, setQuestionResponse] = useState(initialResponse);

  // Use the response from prop (persisted) or local state
  const submittedResponse = initialResponse || questionResponse;
  const minCharacters = 50;
  const maxCharacters = 500;

  // Format time as M:SS
  const formatTime = (seconds) => {
    if (seconds === undefined || seconds === null || isNaN(seconds)) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate timer progress using activity's actual duration
  // timeLimit is in seconds, duration is in minutes (convert to seconds)
  const maxTime = activity?.timerEnabled === true
    ? (activity?.timeLimit || (activity?.duration ? activity.duration * 60 : null) || activity?.config?.timeLimit || 0)
    : 0;
  const timerEnabled = maxTime > 0;

  const handleSubmit = () => {
    if ((questionResponse || '').length >= minCharacters) {
      onSubmit({ response: questionResponse });
    }
  };

  // Activity Ended but Not Responded View
  if (isActivityEnded && !showResults) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBackToList && (
                  <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="flex items-center">
                  <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                  <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 border border-neutral-300 rounded-full">
                  <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full"></div>
                  <span className="text-xs font-medium text-neutral-600">Ended</span>
                </div>
                {sessionCode && (
                  <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                    <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Type Heading */}
        <div className="bg-primary-50 px-6 py-3">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-bold text-primary-600 tracking-wide">OPEN QUESTION</h2>
          </div>
        </div>

        {/* Not Attended Message */}
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="bg-neutral-100 border border-neutral-300 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-neutral-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-neutral-700 mb-2">Activity Ended</h2>
            <p className="text-neutral-600 mb-4">You didn't attend this open question activity.</p>
            <p className="text-sm text-neutral-500">This activity has already ended, so you can no longer submit a response.</p>
            {onBackToList && (
              <button
                onClick={onBackToList}
                className="mt-6 px-6 py-2 bg-neutral-600 hover:bg-neutral-700 text-white font-semibold rounded-lg transition-colors"
              >
                Back to Activities
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Results View
  if (showResults) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBackToList && (
                  <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="flex items-center">
                  <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                  <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isActivityEnded ? (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 border border-neutral-300 rounded-full">
                    <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full"></div>
                    <span className="text-xs font-medium text-neutral-600">Ended</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium text-green-700">Live</span>
                  </div>
                )}
                {sessionCode && (
                  <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                    <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Type Heading */}
        <div className="bg-primary-50 px-6 py-3">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-bold text-primary-600 tracking-wide">OPEN QUESTION</h2>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Success Message */}
          <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-green-800">Response Submitted!</h2>
                <p className="text-sm sm:text-base text-green-700">
                  {isActivityEnded
                    ? 'This activity has ended. Your response has been recorded.'
                    : 'Thank you for sharing your thoughts'}
                </p>
              </div>
            </div>
          </div>

          {/* Response Card */}
          <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-neutral-900 mb-4">{activity.title}</h3>

            {activity.question && (
              <p className="text-sm sm:text-base text-neutral-600 mb-4 sm:mb-6">{activity.question}</p>
            )}

            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-neutral-700">Your Response</h4>
                  <span className="text-xs text-neutral-500">{(submittedResponse || '').length} characters</span>
                </div>
                {submittedResponse ? (
                  <div className="p-3 sm:p-4 bg-primary-50 border border-primary-200 rounded-xl overflow-hidden">
                    <p className="text-sm sm:text-base text-neutral-900 leading-relaxed whitespace-pre-wrap break-words" style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>{submittedResponse}</p>
                  </div>
                ) : (
                  <div className="p-3 sm:p-4 bg-neutral-50 border border-neutral-200 rounded-xl text-center">
                    <p className="text-sm text-neutral-500">Your response has been recorded.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Participation Stats */}
          {(() => {
            // Calculate actual response count - ensure at least 1 if user has responded
            const hasUserResponse = submittedResponse && submittedResponse.trim().length > 0;
            const serverResponseCount = activity.responseCount || 0;
            const displayCount = hasUserResponse ? Math.max(serverResponseCount, 1) : serverResponseCount;
            const totalParticipants = activity.totalParticipants || displayCount || 1;
            const percentage = totalParticipants > 0 ? Math.round((displayCount / totalParticipants) * 100) : 100;

            return (displayCount > 0 || activity.totalParticipants > 0) && (
              <div className="p-3 sm:p-4 bg-neutral-50 rounded-xl">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-neutral-600">
                    {displayCount} of {totalParticipants} participants responded
                  </span>
                  <span className="font-semibold text-primary-600">
                    {percentage}%
                  </span>
                </div>
                <div className="mt-2 w-full h-2 bg-neutral-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 transition-all duration-300"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  }

  // Input View - Clean Design
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBackToList && (
                <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div className="flex items-center">
                <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-green-700">Live</span>
              </div>
              {sessionCode && (
                <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                  <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Activity Type Heading */}
      <div className="bg-primary-50 px-6 py-3">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-lg font-bold text-primary-600 tracking-wide">OPEN QUESTION</h2>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          {/* Left Side - Question & Input */}
          <div className="flex-1 order-2 md:order-1">
            {/* Question Title */}
            <h1 className="text-3xl font-bold text-neutral-900 mb-4 leading-tight">
              {activity.title}
            </h1>

            {activity.question && (
              <p className="text-lg text-neutral-600 mb-8">{activity.question}</p>
            )}

            {/* Response Input Card */}
            <div className="bg-neutral-50 rounded-xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-900">Your Response</h3>
                  <p className="text-sm text-neutral-500">Share your thoughts and experiences</p>
                </div>
              </div>

              <textarea
                value={questionResponse}
                onChange={(e) => {
                  // Allow the change and truncate if exceeds max (handles paste gracefully)
                  const newValue = e.target.value.slice(0, maxCharacters);
                  setQuestionResponse(newValue);
                }}
                onPaste={(e) => {
                  // Explicitly handle paste for mobile compatibility
                  e.preventDefault();
                  const pastedText = e.clipboardData?.getData('text') || '';
                  const currentText = questionResponse || '';
                  const selectionStart = e.target.selectionStart || currentText.length;
                  const selectionEnd = e.target.selectionEnd || currentText.length;

                  // Insert pasted text at cursor position, replacing selection
                  const newText = currentText.slice(0, selectionStart) + pastedText + currentText.slice(selectionEnd);
                  const truncatedText = newText.slice(0, maxCharacters);
                  setQuestionResponse(truncatedText);
                }}
                placeholder="Share your thoughts here... Be specific and provide examples from your experience."
                rows={8}
                className="w-full px-4 py-3 text-neutral-900 bg-white border-2 border-neutral-200 rounded-xl focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 resize-none transition-all"
              />

              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-neutral-500">{(questionResponse || '').length}/{maxCharacters}</span>
                  <span className="text-neutral-300">|</span>
                  <span className="text-neutral-500">Min. {minCharacters} characters</span>
                </div>
                {(questionResponse || '').length >= minCharacters ? (
                  <div className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded-full">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-xs text-green-600 font-medium">Ready to submit</span>
                  </div>
                ) : (
                  <span className="text-xs text-amber-600 font-medium">
                    {minCharacters - (questionResponse || '').length} more characters needed
                  </span>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="mb-8">
              <button
                onClick={handleSubmit}
                disabled={(questionResponse || '').length < minCharacters}
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-neutral-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors"
              >
                Submit Response
              </button>
            </div>

            {/* Tips */}
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <div className="flex gap-3">
                <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-blue-900 mb-2">Tips for a great response:</p>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500">•</span>
                      Be specific and provide real examples
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500">•</span>
                      Share personal insights and lessons learned
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500">•</span>
                      Consider different perspectives
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Timer & Info */}
          <div className="w-full md:w-64 flex-shrink-0 order-1 md:order-2">
            <div className="md:sticky md:top-24">
              {/* Circular Timer - Compact on mobile */}
              {timerEnabled && (
              <div className="flex flex-row md:flex-col items-center gap-4 md:gap-0 mb-4 md:mb-8 p-4 md:p-0 bg-neutral-50 md:bg-transparent rounded-xl md:rounded-none">
                <div className="relative w-24 h-24">
                  {/* Background circle */}
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="48"
                      cy="48"
                      r="44"
                      stroke="#e5e7eb"
                      strokeWidth="8"
                      fill="none"
                    />
                    {/* Progress circle */}
                    <circle
                      cx="48"
                      cy="48"
                      r="44"
                      stroke="#ff8a00"
                      strokeWidth="8"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 44}`}
                      strokeDashoffset={`${2 * Math.PI * 44 * (1 - (timeRemaining / maxTime))}`}
                      className="transition-all duration-1000"
                    />
                  </svg>
                  {/* Timer text */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-primary-600">{formatTime(timeRemaining)}</span>
                  </div>
                </div>
                <div className="flex flex-col items-center md:items-center">
                  <span className="text-sm text-neutral-500 md:mt-2">Time Remaining</span>
                </div>
              </div>
              )}

              {/* Activity Info - Hidden on mobile for cleaner view */}
              <div className="hidden md:block bg-neutral-50 rounded-xl p-4">
                <h3 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Activity Info
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Session:</span>
                    <span className="font-medium text-neutral-700">{sessionCode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Type:</span>
                    <span className="font-medium text-neutral-700">Open Question</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Responded:</span>
                    <span className="font-medium text-neutral-700">18/28</span>
                  </div>
                </div>
              </div>

              {/* Progress - Hidden on mobile */}
              <div className="hidden md:block mt-4 bg-primary-50 rounded-xl p-4 border border-primary-100">
                <h4 className="font-semibold text-primary-900 mb-3">Your Progress</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-primary-700">Characters</span>
                    <span className="font-medium text-primary-900">
                      {(questionResponse || '').length}/{maxCharacters}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-primary-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        (questionResponse || '').length >= minCharacters ? 'bg-green-500' : 'bg-primary-500'
                      }`}
                      style={{ width: `${Math.min(((questionResponse || '').length / maxCharacters) * 100, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-primary-600">
                    {(questionResponse || '').length >= minCharacters
                      ? 'You can submit your response!'
                      : `Write ${minCharacters - (questionResponse || '').length} more characters`}
                  </p>
                </div>
              </div>

              {/* Anonymous Notice - Hidden on mobile */}
              <div className="hidden md:flex mt-4 items-center gap-2 text-sm text-neutral-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Your response is anonymous</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenQuestionActivity;
